import { Flex, Form, Pagination, Table, message } from 'antd';
import type { PaginationProps } from 'antd';
import './index.css';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  UserMngBatchBanParams,
  UserMngItem,
  UserMngListParams,
} from '@/types/userMng';
import {
  batchForbiddenUser,
  batchUnforbiddenUser,
  batchUserDeAlert,
  BatchUserDeAlertParams,
  batchUserFrozen,
  BatchUserFrozenParams,
  batchUserUnFrozen,
  batchUserWarn,
  BatchUserWarnParams,
  getUserMngList,
} from '@/api/userMng';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import SearchForm from './components/SearchForm';
import ActionButtons from './components/ActionButtons';
import { useTableColumns } from './components/TableColumns';
import HistoryDialog from './historyDialog';
import BanConfirmDialog from './banConfirmDialog';
import AlertConfirmDialog, {
  AlertConfirmDialogType,
} from './alertConfirmDialog';
import DeAlertConfirmDialog, {
  DeAlertConfirmDialogType,
} from './deAlertConfirmDialog';
import FrozenConfirmDialog from './frozenConfirmDialog';
import UserTypeDialog, { UserTypeDialogType } from './userTypeDialog';
import GiveVipDialog from './giveVipDialog';
import { batchUpdateUserLimit, batchUpdateUserType } from '@/api/user';
import { useRequest } from 'ahooks';
import { LimitEnum } from './type';
import { trimSearchParams } from '@/utils/stringUtils';

const UserMng = () => {
  const [form] = Form.useForm<Omit<UserMngListParams, 'page' | 'pageSize'>>();
  const navigate = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();

  // State
  const pageSize = isMobile ? 10 : 20;
  // const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [historyUserId, setHistoryUserId] = useState('');
  const [currentActionItem, setCurrentActionItem] = useState<UserMngItem>();

  // Dialog states
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [isBanConfirmDialogOpen, setIsBanConfirmDialogOpen] = useState(false);
  const [banConfirmDialogTitle, setBanConfirmDialogTitle] = useState('');
  const [isForbidden, setIsForbidden] = useState(true);
  const [isAlertConfirmDialogOpen, setIsAlertConfirmDialogOpen] =
    useState(false);
  const [alertConfirmDialogType, setAlertConfirmDialogType] =
    useState<AlertConfirmDialogType>('single');
  const [isDeAlertConfirmDialogOpen, setIsDeAlertConfirmDialogOpen] =
    useState(false);
  const [deAlertConfirmDialogType, setDeAlertConfirmDialogType] =
    useState<DeAlertConfirmDialogType>('single');
  const [isFrozenDialogOpen, setIsFrozenDialogOpen] = useState(false);
  const [isUserTypeDialogOpen, setIsUserTypeDialogOpen] = useState(false);
  const [userTypeDialogType, setUserTypeDialogType] =
    useState<UserTypeDialogType>('single');
  const [isGiveVipDialogOpen, setIsGiveVipDialogOpen] = useState(false);
  const [giveVipUserId, setGiveVipUserId] = useState<string>('');
  const [giveVipUserName, setGiveVipUserName] = useState<string>('');

  // Query
  // const {
  //   data,
  //   isLoading,
  //   refetch: refetchUserMngList,
  // } = useQuery<UserMngListResponse>({
  //   queryKey: ['userMngList', page],
  //   queryFn: async () => {
  //     const params: UserMngListParams = {
  //       ...form.getFieldsValue(),
  //       currentPage: page,
  //       pageSize,
  //     };
  //     const resp = await getUserMngList(params);
  //     if (resp.status !== 200) {
  //       return {
  //         total: 0,
  //         currentPage: 0,
  //         pageSize: 0,
  //         records: [],
  //       };
  //     }
  //     return resp.data;
  //   },
  //   refetchOnWindowFocus: false,
  // });

  const {
    data,
    run: refetchUserMngList,
    loading,
  } = useRequest(async (params = {}) => {
    const payload = {
      ...params,
      currentPage: params.currentPage ?? data?.currentPage ?? 1,
      pageSize,
    } as UserMngListParams;
    const resp = await getUserMngList(payload);
    if (resp.status !== 200) {
      return {
        total: 0,
        currentPage: 0,
        pageSize: 0,
        records: [],
      };
    }
    return resp.data;
  });

  // 需要 trim 的字段列表
  const TRIM_FIELDS: (keyof UserMngListParams)[] = [
    'userNumbers',
    'name',
    'email',
  ] as const;

  // Handlers
  const handleSearch = (values?: Partial<UserMngListParams>) => {
    const formValues = values || form.getFieldsValue();
    const trimmedValues = trimSearchParams<UserMngListParams>(
      formValues,
      TRIM_FIELDS
    );
    
    // 同步更新表单值，确保显示的值也是 trim 后的
    const fieldsToUpdate = TRIM_FIELDS.reduce<Partial<UserMngListParams>>(
      (acc, field) => {
        const originalValue = formValues[field];
        const trimmedValue = trimmedValues[field];
        if (originalValue !== trimmedValue) {
          acc[field] = (trimmedValue ?? '') as any;
        }
        return acc;
      },
      {}
    );
    
    if (Object.keys(fieldsToUpdate).length > 0) {
      form.setFieldsValue(fieldsToUpdate);
    }
    
    void refetchUserMngList({
      ...trimmedValues,
      currentPage: 1,
    });
  };

  const handleDetail = (userId: string) => {
    navigate(`/user-mng/detail?userId=${userId}`);
  };

  const handleBatchBan = () => {
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle('确认批量封禁');
  };

  const handleBatchUnforbidden = () => {
    setIsForbidden(false);
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle('确认批量解封');
  };

  const handleBatchBanConfirm = async (reason: string) => {
    const params: UserMngBatchBanParams = {
      userIds: selectedKeys.join(','),
      forbiddenDesc: reason,
    };
    const resp = await batchForbiddenUser(params);
    if (resp.status !== 200) {
      messageApi.error('封禁失败');
      return;
    }
    messageApi.success('封禁成功');
    void refetchUserMngList();
    setSelectedKeys([]);
    setIsBanConfirmDialogOpen(false);
  };

  const handleBatchUnforbiddenConfirm = async (reason: string) => {
    const params: UserMngBatchBanParams = {
      userIds: selectedKeys.join(','),
      forbiddenDesc: reason,
    };
    const resp = await batchUnforbiddenUser(params);
    if (resp.status !== 200) {
      messageApi.error('解封失败');
      return;
    }
    messageApi.success('解封成功');
    void refetchUserMngList();
    setSelectedKeys([]);
    setIsForbidden(true);
    setIsBanConfirmDialogOpen(false);
  };

  const handleBatchAlertClick = () => {
    setAlertConfirmDialogType('batch');
    setIsAlertConfirmDialogOpen(true);
  };

  const handleBatchDeAlertClick = () => {
    setDeAlertConfirmDialogType('batch');
    setIsDeAlertConfirmDialogOpen(true);
  };

  const handleBatchFrozenClick = () => {
    setIsFrozenDialogOpen(true);
  };

  const handleBatchFrozenConfirm = async (values: { frozenDuration: number; frozenReason: string }) => {
    const params: BatchUserFrozenParams = {
      userIds: selectedKeys.join(','),
      warnDesc: values.frozenReason,
      forzenTime: values.frozenDuration,
      forzenTimeUnit: 'D',
      warnTime: values.frozenDuration,
      timeUnit: 'D',
    };
    const resp = await batchUserFrozen(params);
    if (resp.status !== 200) {
      messageApi.error('冻结失败');
      return;
    }
    messageApi.success('冻结成功');
    void refetchUserMngList();
    setSelectedKeys([]);
    setIsFrozenDialogOpen(false);
  };

  const handleBatchUnFrozenClick = async () => {
    if (selectedKeys.length === 0) return;
    const params = {
      userIds: selectedKeys.join(','),
      warnDesc: '管理员手动解除冻结',
    };
    const resp = await batchUserUnFrozen(params);
    if (resp.status !== 200) {
      messageApi.error('解除冻结失败');
      return;
    }
    messageApi.success('解除冻结成功');
    void refetchUserMngList();
    setSelectedKeys([]);
  };

  const handleBatchUpdateUserType = () => {
    setUserTypeDialogType('batch');
    setIsUserTypeDialogOpen(true);
  };

  const handleUpdateUserTypeConfirm = async (values: {
    userType: number;
    reason: string;
  }) => {
    const params = {
      userIds: selectedKeys,
      userType: values.userType,
      reason: values.reason,
    };
    const resp = await batchUpdateUserType(params);
    if (resp.status !== 200) {
      messageApi.error('打标失败: ' + resp.message);
      return;
    }
    messageApi.success('打标成功');
    setSelectedKeys([]);
    setIsUserTypeDialogOpen(false);
    void refetchUserMngList();
  };

  const handleAlertClick = (record: UserMngItem) => {
    setAlertConfirmDialogType('single');
    setCurrentActionItem(record);
    setSelectedKeys([record.userId]);
    setIsAlertConfirmDialogOpen(true);
  };

  const handleDeAlertClick = (record: UserMngItem) => {
    setDeAlertConfirmDialogType('single');
    setCurrentActionItem(record);
    setSelectedKeys([record.userId]);
    setIsDeAlertConfirmDialogOpen(true);
  };

  const handleBanClick = (record: UserMngItem) => {
    setSelectedKeys([record.userId]);
    setIsForbidden(true);
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle(`确认封禁用户: ${record.name}`);
  };

  const handleUnbanClick = (record: UserMngItem) => {
    setSelectedKeys([record.userId]);
    setIsForbidden(false);
    setIsBanConfirmDialogOpen(true);
    setBanConfirmDialogTitle(`确认解封用户: ${record.name}`);
  };

  const onLimitChange = async (userIds: string, enableLimit: number) => {
    const res = await batchUpdateUserLimit({
      userIds,
      enableLimit
    })

    if (res.status !== 200) {
      messageApi.error(res.message);
      return;
    }

    messageApi.success("操作成功");
    if (userIds.includes(",")) {
      setSelectedKeys([]);
    }
    void refetchUserMngList();
  }

  const handleAlertConfirm = async (values: {
    alertDuration: number;
    alertReason: string;
  }) => {
    const params: BatchUserWarnParams = {
      userIds: selectedKeys.length ? selectedKeys.join(',') : '',
      warnDesc: values.alertReason,
      warnTime: values.alertDuration,
      timeUnit: 'D',
    };
    const resp = await batchUserWarn(params);
    if (resp.status !== 200) {
      messageApi.error('警告失败: ' + resp.message);
      return;
    }
    messageApi.success('警告成功');
    setSelectedKeys([]);
    setIsAlertConfirmDialogOpen(false);
    void refetchUserMngList();
  };

  const handleDeAlertConfirm = async (values: { deAlertReason: string }) => {
    const params: BatchUserDeAlertParams = {
      userIds: selectedKeys.length ? selectedKeys.join(',') : '',
      warnDesc: values.deAlertReason,
    };
    const resp = await batchUserDeAlert(params);
    if (resp.status !== 200) {
      messageApi.error('解除警告失败: ' + resp.message);
      return;
    }
    messageApi.success('解除警告成功');
    setSelectedKeys([]);
    setIsDeAlertConfirmDialogOpen(false);
    void refetchUserMngList();
  };

  // 处理赠送会员
  const handleGiveVip = (record: UserMngItem) => {
    setGiveVipUserId(record.userId);
    setGiveVipUserName(record.name || '');
    setIsGiveVipDialogOpen(true);
  };

  // Table columns
  const columns = useTableColumns({
    onDetail: handleDetail,
    onHistory: (userId) => {
      setHistoryUserId(userId);
      setIsHistoryDialogOpen(true);
    },
    onAlert: handleAlertClick,
    onDeAlert: handleDeAlertClick,
    onBan: handleBanClick,
    onUnban: handleUnbanClick,
    onInviteNumClick: (userNumber) => {
      setShowAdvanced(true);
      form.resetFields();
      form.setFieldsValue({ invitorUserSuppId: userNumber.toString() });
      handleSearch({ invitorUserSuppId: userNumber.toString() });
    },
    onLimitChange,
    onGiveVip: handleGiveVip,
  });

  // 总条目
  const showTotal: PaginationProps['showTotal'] = (total) =>
    `共 ${total} 条数据`;

  const [showAdvanced, setShowAdvanced] = useState(false);
  const toggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  return (
    <div className="user-mng-container">
      {contextHolder}
      <SearchForm
        form={form}
        onSearch={handleSearch}
        showAdvanced={showAdvanced}
        ontoggleAdvanced={toggleAdvanced}
      />

      <ActionButtons
        selectedKeys={selectedKeys}
        onBatchAlert={handleBatchAlertClick}
        onBatchDeAlert={handleBatchDeAlertClick}
        onBatchFrozen={handleBatchFrozenClick}
        onBatchUnFrozen={handleBatchUnFrozenClick}
        onBatchBan={handleBatchBan}
        onBatchUnforbidden={handleBatchUnforbidden}
        onBatchUpdateUserType={handleBatchUpdateUserType}
        onBatchLimitOpen={() => onLimitChange(selectedKeys.join(","), LimitEnum.limit)}
        onBatchLimitClose={() => onLimitChange(selectedKeys.join(","), LimitEnum.noLimit)}
      />

      <Table
        loading={loading}
        size="small"
        columns={columns}
        dataSource={data?.records}
        rowKey="userId"
        scroll={{ x: 'max-content' }}
        pagination={{
          size: isMobile ? 'small' : 'default',
          total: data?.total || 0,
          pageSize,
          current: data?.currentPage,
          showTotal,
          onChange: (current) => refetchUserMngList({ currentPage: current }),
          showSizeChanger: false
        }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          type: 'checkbox',
          fixed: true,
          onChange: (selectedRowKeys) => {
            setSelectedKeys(selectedRowKeys as string[]);
          },
        }}
      />

      {/* <Flex justify="flex-end" className="table-pagination">
        <Pagination
          size={isMobile ? 'small' : 'default'}
          total={data?.total || 0}
          pageSize={pageSize}
          current={page}
          showTotal={showTotal}
          onChange={(page) => {
            setPage(page);
          }}
          showSizeChanger={false}
        />
      </Flex> */}

      <HistoryDialog
        userId={historyUserId}
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
      />

      <BanConfirmDialog
        isForbidden={isForbidden}
        isOpen={isBanConfirmDialogOpen}
        onClose={() => setIsBanConfirmDialogOpen(false)}
        title={banConfirmDialogTitle}
        onConfirm={
          isForbidden ? handleBatchBanConfirm : handleBatchUnforbiddenConfirm
        }
      />

      <AlertConfirmDialog
        type={alertConfirmDialogType}
        isOpen={isAlertConfirmDialogOpen}
        onClose={() => setIsAlertConfirmDialogOpen(false)}
        onConfirm={handleAlertConfirm}
        user={currentActionItem}
      />

      <DeAlertConfirmDialog
        type={deAlertConfirmDialogType}
        isOpen={isDeAlertConfirmDialogOpen}
        onClose={() => setIsDeAlertConfirmDialogOpen(false)}
        onConfirm={handleDeAlertConfirm}
        user={currentActionItem}
      />

      <FrozenConfirmDialog
        isOpen={isFrozenDialogOpen}
        onClose={() => setIsFrozenDialogOpen(false)}
        onConfirm={handleBatchFrozenConfirm}
      />

      <UserTypeDialog
        type={userTypeDialogType}
        isOpen={isUserTypeDialogOpen}
        onClose={() => setIsUserTypeDialogOpen(false)}
        onConfirm={handleUpdateUserTypeConfirm}
        user={currentActionItem}
      />

      <GiveVipDialog
        open={isGiveVipDialogOpen}
        userId={giveVipUserId}
        userName={giveVipUserName}
        onClose={() => setIsGiveVipDialogOpen(false)}
        onSuccess={() => {
          refetchUserMngList();
        }}
      />
    </div>
  );
};

export default UserMng;
